import { AsyncPipe, DecimalPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, Signal, WritableSignal, computed, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { MatTab, MatTabGroup } from '@angular/material/tabs';
import { LetDirective } from '@ngrx/component';
import { isEqual } from 'lodash';
import { Observable } from 'rxjs';
import { map, mergeMap, take, tap } from 'rxjs/operators';

import { IndustryCatalogType, Pagination, PaginationMetadata } from '@core/shared/domain';
import { DatePipesModule, HeaderElementDirective, filterNull } from '@core/shared/util';
import {
  AlertComponent,
  CatalogCovinBadgeComponent,
  DropdownComponent,
  NavigateBackButtonComponent,
  RootModuleNavComponent,
  SelectOption,
  SpinnerComponent,
  ViewModule,
} from '@core/ui';
import {
  IndustryCatalog,
  IndustryCatalogManufacturer,
  IndustryCatalogSupplier,
  PackungseinheitenMapping,
  PlattformPackungseinheitMapping,
  UpdateIndustryCatalog,
  UpdateItem,
  UpdateSupplierOrManufacturer,
  UpdateUnit,
  injectPlatformSuppliersQuery,
} from '@mp/content-manager/industriekataloge/data-access';
import { CatalogTaskComponent } from '@mp/shared/ui';
import { CatalogSupplierSelectorComponent } from '@mp/supplier-management/common/ui';
import { PlatformPackagingUnitsPermissions } from '@mp/system/platform-packaging-units/feature';
import { LoginContextService } from '@mpk/shared/data-access';
import { PlatformSupplierBasic } from '@mpsm/shared/domain';

import { IndustriekatalogeFacade } from '../industriekataloge.facade';
import { IndustryCatalogsPermissions } from '../permissions';

import {
  EntityPaginationEvent,
  NavigateTo,
  TabAufgabenComponent,
} from './industriekatalog-tabs/tab-aufgaben.component';

type NavigateToLabel = 'supplier' | 'manufacturer' | 'packagingUnits';

const navigateToMap: Record<NavigateToLabel, NavigateTo> = {
  supplier: {
    elementId: 'supplier',
  },
  manufacturer: {
    elementId: 'manufacturer',
  },
  packagingUnits: {
    elementId: 'packagingUnits',
  },
};

@Component({
  selector: 'mp-industriekatalog-detail',
  templateUrl: './industriekatalog-detail.component.html',
  styleUrl: './industriekatalog-detail.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AsyncPipe,
    DecimalPipe,
    ReactiveFormsModule,
    LetDirective,

    MatIcon,
    MatTab,
    MatTabGroup,

    HeaderElementDirective,
    RootModuleNavComponent,
    NavigateBackButtonComponent,
    AlertComponent,
    CatalogSupplierSelectorComponent,
    DropdownComponent,
    CatalogTaskComponent,
    CatalogCovinBadgeComponent,
    TabAufgabenComponent,
    SpinnerComponent,
    DatePipesModule,
    ViewModule,
  ],
})
export class IndustriekatalogDetailComponent {
  protected readonly tabAufgabenComponent: Signal<TabAufgabenComponent> = viewChild.required(TabAufgabenComponent);

  private id?: string;

  disabledAfterSave = false;

  readonly katalog$: Observable<IndustryCatalog | null>;
  readonly kataloglieferanten$: Observable<IndustryCatalogSupplier[]> = this.facade.kataloglieferanten$;
  readonly kataloghersteller$: Observable<IndustryCatalogManufacturer[]> = this.facade.kataloghersteller$;
  readonly packungseinheiten$: Observable<PackungseinheitenMapping> = this.facade.einheiten$;
  readonly lieferantenPagination$: Observable<PaginationMetadata | undefined> = this.facade.lieferantenPagination$;
  readonly herstellerPagination$: Observable<PaginationMetadata | undefined> = this.facade.herstellerPagination$;
  readonly packagingUnitPagination$: Observable<PaginationMetadata | undefined> = this.facade.packagingUnitPagination$;

  readonly isLoading$: Observable<boolean> = this.facade.isLoading$;

  protected readonly hasManageIndustryCatalogsPermission = this.loginContextService.permission(
    IndustryCatalogsPermissions.ManageIndustryCatalogs,
  );

  // TODO: Permissions of another module must not be checked here. PlatformPackagingUnits in fact is ContentManagement and should be moved here.
  readonly hasManagePlatformPackagingUnitsPermission = this.loginContextService.permission(
    PlatformPackagingUnitsPermissions.ManagePlatformPackagingUnits,
  );

  readonly catalogsOpenTasks$ = this.facade.catalogsOpenTasks$;

  readonly searchTerm: WritableSignal<string> = signal('');
  readonly platformSupplierQuery = injectPlatformSuppliersQuery(this.searchTerm);

  readonly platformSuppliers: Signal<PlatformSupplierBasic[]> = computed(
    () => this.platformSupplierQuery.data()?.data ?? [],
  );

  readonly isLoadingPlatformSuppliers: Signal<boolean> = this.platformSupplierQuery.isLoading;

  readonly catalogTypeOptions: SelectOption<string | undefined>[] = [
    { label: 'Einkaufsgemeinschaft', value: 'PurchasingGroup' },
    { label: 'Händler', value: 'Dealer' },
    { label: 'Hersteller', value: 'Manufacturer' },
    { label: 'Hybrid', value: 'Hybrid' },
    { label: 'Keine Kategorie', value: undefined, isResetOption: true },
  ];

  protected readonly supplierIdControl: FormControl<string | undefined> = this.fb.control(undefined);
  protected readonly catalogTypeControl: FormControl<IndustryCatalogType | undefined> = this.fb.control(undefined);

  protected readonly form = this.fb.group({
    supplierId: this.supplierIdControl,
    catalogType: this.catalogTypeControl,
  });

  private readonly formInitialValue: WritableSignal<UpdateIndustryCatalog | undefined> = signal<
    UpdateIndustryCatalog | undefined
  >(undefined);

  private readonly formValue: Signal<UpdateIndustryCatalog | undefined> = toSignal(
    this.form.valueChanges.pipe(map(() => this.form.getRawValue())) as Observable<UpdateIndustryCatalog>,
  );

  protected readonly hasFormValueChanged: Signal<boolean> = computed(() => {
    if (!this.formInitialValue() || !this.formValue()) {
      return false;
    }

    return !isEqual(this.formInitialValue(), this.formValue());
  });

  constructor(
    private readonly fb: NonNullableFormBuilder,
    private readonly facade: IndustriekatalogeFacade,
    private readonly loginContextService: LoginContextService,
  ) {
    this.katalog$ = facade.urlId$.pipe(
      take(1),
      mergeMap((id) => {
        this.id = id;
        return this.facade.getKatalogDetail(id);
      }),
      filterNull(),
      tap((catalog) => this.patchFormInitialState(catalog)),
      takeUntilDestroyed(),
    );
  }

  update(): void {
    if (!this.id) {
      throw new Error('":industriekatalogId" could not be read from route!');
    }

    const updateDto: UpdateIndustryCatalog = this.form.getRawValue();

    this.facade.updateIndustriekatalog(this.id, updateDto);

    if (updateDto.supplierId) {
      this.disabledAfterSave = true;
    }
  }

  onLieferantChanges(lieferant?: PlatformSupplierBasic): void {
    this.form.patchValue({ supplierId: lieferant?.supplierId });
  }

  onSearchTermChange(searchTerm: string): void {
    this.searchTerm.set(searchTerm);
  }

  updateLieferantOrHersteller({ isSupplierUpdated, designation, platformSupplierId }: UpdateItem): void {
    if (!this.id) {
      throw new Error('":industriekatalogId" could not be read from route!');
    }

    const updateSupplier: UpdateSupplierOrManufacturer = {
      id: this.id,
      designation,
      platformSupplierId,
    };

    this.facade.updateLieferantOrHersteller(isSupplierUpdated, updateSupplier);
  }

  updateEinheiten({ unit, mappedShortName }: UpdateUnit): void {
    if (!this.id) {
      throw new Error('":industriekatalogId" could not be read from route!');
    }

    if (unit.viaDataSource && (!mappedShortName || mappedShortName === unit.mappedShortName)) {
      return;
    }

    const updatePackungseinheiten: PlattformPackungseinheitMapping = {
      articleType: unit.articleType,
      dataSource: unit.dataSource,
      catalogId: unit.catalogId,
      shortName: unit.shortName,
      mappedLongName: unit.mappedLongName,
      mappedShortName: mappedShortName,
    };

    this.facade.updatePackungseinheit(updatePackungseinheiten);
  }

  getNextPage({ isLieferantPagination, pagination }: EntityPaginationEvent) {
    if (!this.id) {
      throw new Error('":industriekatalogId" could not be read from route!');
    }

    isLieferantPagination
      ? this.facade.getKatalogLieferanten(this.id, pagination)
      : this.facade.getKatalogHersteller(this.id, pagination);
  }

  getNextPackagingUnitPage(pagination: Pagination): void {
    if (!this.id) {
      throw new Error('":klinikkatalogId" could not be read from route!');
    }
    this.facade.getEinheiten(this.id, pagination);
  }

  close(): void {
    this.facade.navigateBack();
  }

  // To make sure that the scrolling works after a second click as well, we need the change to be in
  // the form of an object, because Angular inputs do not trigger twice if the input type is simple
  // e.g. a string.
  navigateTo(element: NavigateToLabel): void {
    const navigateToElement: NavigateTo = {
      ...navigateToMap[element],
    };
    this.tabAufgabenComponent().navigateToElementId(navigateToElement);
  }

  private patchFormInitialState({ supplier, type }: IndustryCatalog): void {
    const initialValue: UpdateIndustryCatalog = {
      catalogType: type,
      supplierId: supplier?.supplierId,
    };

    this.formInitialValue.set(initialValue);

    this.form.reset();
    this.form.patchValue(initialValue);
  }
}
